// Convert data to CSV format
import { useGlobalStore } from "@/store/global";
import date from "date-and-time";

const globalStore = useGlobalStore();
const vesselDate = "MM/DD/YYYY";
const vesselTime = "HH:mm";

const formatter = (dateString: string, format?: string): string => {
  const dateTime = new Date(removeOffsetFromDateString(dateString));
  const formattedDate = date.format(dateTime, format ?? vesselDate);
  const formattedTime = date.format(dateTime, format ?? vesselTime);
  return `${formattedDate} - ${formattedTime}`;
};

const removeOffsetFromDateString = (dateStr: string): string => {
  const format = dateStr.substring(0, 16);
  return format;
};

// Convert data to CSV format
const convertToCSV = (values: any[], headerMapping: Record<string, string>): string => {
  if (values.length === 0) return "";
  // Extract headers from the keys of the first object and map them
  const headers = Object.keys(values[0]);
  const readableHeaders = headers.map((header): string => headerMapping[header] || header);
  // Create the header row
  const headerRow = readableHeaders.join(",");
  // Create rows for each object in the values array
  const valueRows = values.map((row) =>
    headers
      .map((header): string => {
        const value = row[header];
        let updatedValue: any = "";
        if (typeof value === "boolean") {
          updatedValue = value ? globalStore.sitecoreData.common.yes : globalStore.sitecoreData.common.no;
        } else if (
          header.includes("Date") ||
          header === "docCutOff" ||
          header === "vesselEta" ||
          header === "vesselEtd"
        ) {
          updatedValue = value && value != "" && value != "No" ? formatter(value) : value;
        } else if (header === "shippingLines") {
          updatedValue = value && value.length ? value.join(" - ") : "";
        } else {
          updatedValue = `"${String(value).replace(/"/g, '""')}"`;
        }
        // Handle boolean values and quotes for strings with commas
        return updatedValue;
      })
      .join(",")
  );
  // Combine header row and value rows into a CSV string
  return [headerRow, ...valueRows].join("\n");
};

// Download CSV file
export const downloadCSV = (values: any[], headerMapping: any[], filename: string, sourceName: string): void => {
  const transformedHeaderMapping = [...headerMapping];
  if (sourceName === "Vessel-Schedule") {
    const targetId = "shippingLines";
    const count = transformedHeaderMapping.filter((item) => item.id === targetId).length;
    if (!count) {
      transformedHeaderMapping.push({
        id: "shippingLines",
        label: globalStore.sitecoreData.table_columns.schedule.line
      });
    }
  }
  const columnName: Record<string, string> = transformedHeaderMapping.reduce(
    (acc: Record<string, string>, item: any): Record<string, string> => {
      acc[item.id] = item.label;
      return acc;
    },
    {}
  );
  const filteredVesselSchedules = values.map((item: any): any => {
    const filteredItem: any = {};
    const transformedItem = { ...item }; // Clone item to avoid mutating original

    if (!item.gateOpenDateTimeLocal || item.gateOpenDateTimeLocal !== item.startReceiveDateTimeLocal) {
      transformedItem.gateOpenDateTimeLocal = item.startReceiveDateTimeLocal || "";
    }

    if (!item.docCutOff || item.docCutOff !== item.hazardousCutoffDateTimeLocal) {
      transformedItem.docCutOff = item.hazardousCutoffDateTimeLocal || "";
    }
    if (!item.shippingLineOperator || item.shippingLineOperator !== item.vesselOperator) {
      transformedItem.shippingLineOperator = item.vesselOperator ? item.vesselOperator.toUpperCase() : "";
    }
    if (!item.vesselEta || item.vesselEta !== item.expectedTimeArrivalDateTimeLocal) {
      transformedItem.vesselEta = item.expectedTimeArrivalDateTimeLocal || "";
    }
    if (!item.appointmentDateTimeLocal || item.appointmentDateTimeLocal !== item.roadGateAppointmentDateTimeLocal) {
      transformedItem.appointmentDateTimeLocal = item.roadGateAppointmentDateTimeLocal || "No";
    }
    if (!item.vesselEtd || item.vesselEtd !== item.estimatedDepartureDateTimeLocal) {
      transformedItem.vesselEtd = item.estimatedDepartureDateTimeLocal || "";
    }
    if (!item.vesselType || item.vesselType === "") {
      transformedItem.vesselType = item.vesselClassification || "";
    }

    // Only copy allowed keys
    Object.keys(transformedItem).forEach((key) => {
      if (columnName[key]) {
        filteredItem[key] = transformedItem[key];
      }
    });

    return filteredItem;
  });
  const csvContent = convertToCSV(filteredVesselSchedules, columnName);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
